import { useEffect, useState } from 'react';
import { ITypedBaseResponse } from '@nkr/models/base-response';
import { IOpoAddressesData, IOpoAddressRequest } from '@nkr/models/opo-address';
import { IOpoSaveResult } from '@nkr/models/opo-document';
import { AppActionCreators } from '@nkr/store/application';
import { AuthActionCreators } from '@nkr/store/authorization';
import { DashActionCreators, IAddressValidationResponse } from '@nkr/store/index';
import { useAppHookContext } from '@nkr/utils/rest-api';
import usePortalOpo from './use-portal-opo';

interface IRetVal {
    data: IOpoAddressesData | null,
    loading: boolean,
    error: string,
    save: (addressId: number | null, request: IOpoAddressRequest) => Promise<IOpoSaveResult>,
    remove: (addressId: number) => Promise<IOpoSaveResult>,
    validateAddress: (address: string, address2: string, city: string, state: string, postalCode: string) => Promise<IAddressValidationResponse>
}

const useOpoAddresses = (): IRetVal => {
    const { dispatch } = useAppHookContext();
    const { opoId } = usePortalOpo();
    const [data, setData] = useState<IOpoAddressesData | null>(null);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');
    const [refreshKey, setRefreshKey] = useState<number>(0);

    const baseUrl = `${__API__}/opo/${opoId}/addresses`;

    useEffect(() => {
        if (!opoId) {
            return;
        }

        let isCurrent = true;

        const fetchData = async () => {
            setLoading(true);
            setError('');

            await dispatch(AppActionCreators.showLoadingAsync('Getting addresses, one moment please...'));

            try {
                const response: ITypedBaseResponse<IOpoAddressesData> = await dispatch(AuthActionCreators.apiGet({ url: baseUrl }));

                if (!isCurrent) {
                    return;
                }

                if (response && response.data.success) {
                    setData(response.data.results);
                } else {
                    setError('Unable to load addresses');
                }
            } catch (err) {
                if (isCurrent) {
                    setError('Unable to load addresses');
                }
            } finally {
                if (isCurrent) {
                    setLoading(false);
                    dispatch(AppActionCreators.hideLoading());
                }
            }
        };

        fetchData();

        return () => {
            isCurrent = false;
            dispatch(AppActionCreators.hideLoading());
        };
    }, [opoId, refreshKey]);

    const runAction = async (message: string, action: () => Promise<ITypedBaseResponse<any>>, failureMessage: string): Promise<IOpoSaveResult> => {
        await dispatch(AppActionCreators.showLoadingAsync(message));

        try {
            const response = await action();

            if (response && response.data.success) {
                setRefreshKey(x => x + 1);
                return { success: true, message: '' };
            }

            return { success: false, message: (response && response.data.message) || failureMessage };
        } catch (err) {
            return { success: false, message: failureMessage };
        } finally {
            dispatch(AppActionCreators.hideLoading());
        }
    };

    const save = (addressId: number | null, request: IOpoAddressRequest) => runAction(
        'Saving address, one moment please...',
        () => dispatch(addressId
            ? AuthActionCreators.apiPut({ url: `${baseUrl}/${addressId}`, data: request })
            : AuthActionCreators.apiPost({ url: baseUrl, data: request })),
        'Unable to save the address');

    const remove = (addressId: number) => runAction(
        'Deleting address, one moment please...',
        () => dispatch(AuthActionCreators.apiDelete({ url: `${baseUrl}/${addressId}` })),
        'Unable to delete the address');

    const validateAddress = async (address: string, address2: string, city: string, state: string, postalCode: string): Promise<IAddressValidationResponse> => {
        await dispatch(AppActionCreators.showLoadingAsync('Validating address, one moment please...'));

        try {
            return await dispatch(DashActionCreators.validateAddress(address, address2, city, state, postalCode));
        } finally {
            dispatch(AppActionCreators.hideLoading());
        }
    };

    return { data, loading, error, save, remove, validateAddress };
}

export default useOpoAddresses;
